import {
  Box,
  Paper,
  TextField,
  IconButton,
  List,
  ListItem,
  CircularProgress,
} from '@mui/material';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useChatStream } from '../../hooks/useChatStream';
import { IMessages } from '../../interface/message';
import styles from './styles.module.scss';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { useAppContext } from '../../context/AppContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ASSETS } from '../../const/assets';
import FormRegister from '../FormRegister';
import { useNews } from '../../hooks/useNews';

function ChatScreen() {
  const { selectedRole, setSelectedRole } = useAppContext();
  const [isNewsLatest, setIsNewsLatest] = useState(false);
  const isKey = '';
  const { data: news, isLoading: isLoadingNews } = useNews(
    selectedRole,
    isNewsLatest,
    isKey
  );

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<IMessages[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [openModal, setOpenModal] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const { sendMessage } = useChatStream();

  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const role = searchParams.get('role');
    const isNewsLatest = searchParams.get('isNewsLatest');

    if (role) {
      setSelectedRole(role);
      if (isNewsLatest === 'true') {
        setIsNewsLatest(true);
      }
    } else {
      navigate('/');
    }
  }, [searchParams, setSelectedRole, navigate]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleChunkCallback = useCallback((chunk: string) => {
    setMessages((prevMessages) => {
      const newMessages = structuredClone(prevMessages);
      const lastMessage = newMessages[newMessages.length - 1];
      if (!lastMessage.content.endsWith(chunk)) {
        lastMessage.content = lastMessage.content + chunk;
      }
      return newMessages;
    });
  }, []);

  const sendMessageWithSuggestion = (suggestion: string) => {
    setMessages([
      ...messages,
      {
        role: 'user',
        content: suggestion,
      },
      {
        role: 'assistant',
        content: '',
      },
    ]);
    setNewMessage('');

    if (suggestion === 'Tôi muốn đăng ký nhận tin mới') {
      setMessages([
        ...messages,
        {
          role: 'user',
          content: suggestion,
        },
        {
          role: 'assistant',
          content: 'Biểu mẫu',
        },
      ]);
      setNewMessage('');
    } else {
      setMessages([
        ...messages,
        {
          role: 'user',
          content: suggestion,
        },
        {
          role: 'assistant',
          content: '',
        },
      ]);
      setNewMessage('');
      sendMessage(
        {
          senderTitle: selectedRole.toString(),
          messages: [{ role: 'user', content: suggestion }],
        },
        handleChunkCallback
      );
    }
  };

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      if (newMessage.trim() === 'Tôi muốn đăng ký nhận tin mới') {
        sendMessageWithSuggestion(newMessage);
      } else {
        setMessages([
          ...messages,
          {
            role: 'user',
            content: newMessage,
          },
          {
            role: 'assistant',
            content: '',
          },
        ]);
        setNewMessage('');

        sendMessage(
          {
            senderTitle: selectedRole.toString(),
            messages: [{ role: 'user', content: newMessage }],
          },
          handleChunkCallback
        );
      }
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    // Function to update the window height
    const updateHeight = () => {
      setWindowHeight(window.innerHeight);
    };

    // Add event listeners
    window.addEventListener('resize', updateHeight);

    // For mobile browsers, also listen for orientation changes
    window.addEventListener('orientationchange', updateHeight);

    // Initial setup
    updateHeight();

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('orientationchange', updateHeight);
    };
  }, []);

  return (
    <>
      <div
        className={`flex items-center justify-center ${
          isFormOpen ? 'md:justify-end' : 'md:justify-start'
        }`}
        style={{ height: `${windowHeight}px` }}
      >
        <img
          className="md:w-screen hidden md:block md:absolute left-0 top-0 md:h-screen object-cover"
          src={!isFormOpen ? ASSETS.bgChat : ASSETS.bsm}
          alt="bgChat"
        />
        <div
          className="absolute md:top-5 md:left-20 cursor-pointer w-full h-[58px] md:h-auto md:w-auto top-0 left-0 pl-5 md:pl-0 bg-[#4696FF] flex items-center md:justify-start md:bg-transparent"
          onClick={() => {
            if (isFormOpen) {
              setIsFormOpen(false);
              setIsSuccess(false);
            } else {
              navigate('/');
            }
          }}
        >
          <img src={ASSETS.icArrowLeft} alt="icArrowLeft" />
        </div>
        {isFormOpen && (
          <FormRegister isSuccess={isSuccess} setIsSuccess={setIsSuccess} />
        )}
        {isLoadingNews ? (
          <div className="flex items-center justify-center h-full">
            <CircularProgress />
          </div>
        ) : (
          <>
            {!isFormOpen && (
              <div className="px-5 md:ml-[12%] xl:ml-[17.2%] relative z-10">
                <video
                  className="w-full h-full object-cover rounded-lg shadow-lg md:max-h-[85vh] max-h-[75vh]"
                  controls
                  autoPlay
                  muted
                  loop
                >
                  <source src={news?.videoLink} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
          </>
        )}
        {!openModal && (
          <div className="flex gap-4 rounded-t-2xl w-full bg-white fixed bottom-0 justify-center">
            <div
              className="z-50 justify-center bg-white p-3 md:p-2 md:rounded-full cursor-pointer md:fixed md:right-16 md:bottom-1 lg:bottom-5 md:w-auto"
              onClick={handleOpenModal}
            >
              <img src={ASSETS.icAi} alt="icAi" className="w-10 h-10" />
            </div>
            <div
              className="z-50 justify-center bg-white p-3 md:p-2 md:rounded-full cursor-pointer md:fixed bottom-0 md:right-16 md:bottom-18 lg:bottom-24 md:w-auto"
              onClick={() => {
                setIsFormOpen(true);
                handleCloseModal();
              }}
            >
              <img src={ASSETS.icMail} alt="icMail" className="w-10 h-10" />
            </div>
            <div
              className="z-50 justify-center bg-white p-3 md:p-2 md:rounded-full cursor-pointer md:fixed bottom-0 md:right-16 md:bottom-36 lg:bottom-44 md:w-auto"
              onClick={() => {
                navigate(
                  `/news?role=${selectedRole}&isNewsLatest=${isNewsLatest}`
                );
              }}
            >
              <img src={ASSETS.icNews} alt="icNews" className="w-10 h-10" />
            </div>
          </div>
        )}

        <div
          className={`fixed md:bottom-16 md:right-16 transition-all duration-300 z-50 ${
            openModal ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
          }`}
          style={{ transformOrigin: 'bottom right' }}
        >
          <Box
            sx={{
              width: { xs: '100vw', md: '420px' },
              height: {
                xs: `${windowHeight}px`,
                md: '85vh',
              },
              backgroundColor: 'white',
              borderRadius: { xs: '0', md: 10 },
              boxShadow: 3,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div className="mt-8 pb-2 border-[#ECECEC] border-b px-6">
              <div className="flex items-center gap-4">
                <img
                  src={ASSETS.icBack}
                  alt="icBack"
                  className="w-6 h-6 cursor-pointer"
                  onClick={handleCloseModal}
                />
                <img src={ASSETS.icAi} alt="icAi" className="w-10 h-10" />
                <div>
                  <span className="text-[#3369FF] font-bold text-xl">
                    Chat bot
                  </span>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-[#3ABF38] rounded-full" />
                    <span className="text-[#3ABF38] text-sm font-bold">
                      Online
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1 mb-2 sm:mb-4 overflow-auto p-2 sm:p-4">
              {messages.length === 0 && (
                <div className="flex items-center justify-center flex-col">
                  <img src={ASSETS.icM} alt="icM" className="w-7 h-7" />
                  <span className="text-sm mt-1">Bạn đang quan tâm?</span>
                  <div
                    className="text-sm mt-4 mb-2 bg-[#F4F4F4] rounded-3xl py-2 flex items-center justify-center w-full cursor-pointer"
                    onClick={() =>
                      sendMessageWithSuggestion(
                        'Tôi muốn tìm hiểu thêm thông tin về dự án này'
                      )
                    }
                  >
                    Tôi muốn tìm hiểu thêm thông tin về dự án này
                  </div>
                  <div
                    className="text-sm bg-[#F4F4F4] rounded-3xl py-2 flex items-center justify-center w-full cursor-pointer"
                    onClick={() =>
                      sendMessageWithSuggestion('Tôi muốn đăng ký nhận tin mới')
                    }
                  >
                    Tôi muốn đăng ký nhận tin mới
                  </div>
                </div>
              )}
              <List>
                {messages.map((message, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      justifyContent:
                        message.role === 'user' ? 'flex-end' : 'flex-start',
                      position: 'relative',
                    }}
                  >
                    <Paper
                      elevation={1}
                      sx={{
                        p: { xs: 1, sm: 1.5 },
                        backgroundColor:
                          message.role === 'user' ? '#3369FF' : '#EEEEEE',
                        maxWidth: '90%',
                        wordBreak: 'break-word',
                        color:
                          message.role === 'user'
                            ? 'white'
                            : message.content === 'Biểu mẫu'
                            ? '#0045BF'
                            : '#434343',
                        borderRadius:
                          message.role === 'user'
                            ? '25px 25px 0 25px'
                            : '25px 25px 25px 0',
                        margin:
                          message.role === 'user' ? '0px' : '0 0 15px 15px',
                        cursor:
                          message.content === 'Biểu mẫu'
                            ? 'pointer'
                            : 'default',
                      }}
                      onClick={() => {
                        if (message.content === 'Biểu mẫu') {
                          setIsFormOpen(true);
                          handleCloseModal();
                        }
                      }}
                    >
                      <div>
                        {message.role === 'assistant' &&
                        message.content === '' ? (
                          <span style={{ marginLeft: 4 }}>
                            <span className={styles.loadingDot}></span>
                            <span className={styles.loadingDot}></span>
                            <span className={styles.loadingDot}></span>
                          </span>
                        ) : (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm, remarkMath]}
                            rehypePlugins={[rehypeKatex]}
                            components={{
                              code: ({ children, ...props }) => {
                                return (
                                  <code
                                    className={`${styles.inlineCode} ${props.className}`}
                                    {...props}
                                  >
                                    {children}
                                  </code>
                                );
                              },
                            }}
                          >
                            {message.content}
                          </ReactMarkdown>
                        )}
                      </div>
                    </Paper>
                    {message.role !== 'user' && (
                      <div
                        className="absolute -bottom-2 left-0 p-1 bg-white rounded-full"
                        style={{
                          boxShadow: '0px 1.96px 1.96px 0px #0000000D',
                        }}
                      >
                        <img
                          src={ASSETS.icAi}
                          alt="AI Avatar"
                          className="w-6 h-6"
                        />
                      </div>
                    )}
                  </ListItem>
                ))}
                <div ref={messagesEndRef} />
              </List>
            </div>

            <Box
              sx={{
                display: 'flex',
                gap: { xs: 0.5, sm: 1 },
                px: { xs: 0, sm: 4 },
                pb: { xs: 0, sm: 4 },
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Xin chào! Tôi có thể giúp gì cho bạn?"
                className="text-[#3E3E3E]"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSendMessage();
                  }
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim()}
                        sx={{
                          width: 40,
                          height: 40,
                        }}
                      >
                        <img
                          src={
                            newMessage.trim()
                              ? ASSETS.icSend
                              : ASSETS.icSendDisabled
                          }
                          alt="icSend"
                        />
                      </IconButton>
                    ),
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1rem',
                    borderRadius: { xs: '0', sm: '30px' },
                    boxShadow: '5px 4px 20px 0px #00000021',
                    '& fieldset': { border: 'none' },
                    '& input': { paddingLeft: '22px' },
                  },
                  '& .MuiInputBase-root': {
                    paddingRight: '8px',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#3E3E3E',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          </Box>
        </div>
      </div>
    </>
  );
}

export default ChatScreen;
